import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ClipLoader from "react-spinners/ClipLoader";
import textStyles from "../Text.module.css";
import Popup from "../../Dialog/Popup";

const Main = styled.div`
  padding: 0.5em;
  width: 440px;
  height: calc(100vh - 85px);
  background-color: #181d25;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
`;

const Group = styled.div`
  display: flex;
  padding: 0.5em;
  flex-direction: column;
  margin-bottom: 2em;
  background-color: #1f252f;
  border-radius: 4px;
  box-shadow:
    0px 3px 3px -20px rgba(0, 0, 0, 0.2),
    0px 5px 20px 0px rgba(0, 0, 0, 0.14),
    0px 4px 20px 8px rgba(0, 0, 0, 0.12);
`;

const Title = styled.div`
  font-size: 1.1em;
  background-color: #282f3c;
  padding: 0.3em;
  border-radius: 4px;
  text-align: center;
  border-bottom: 1px solid #161a21;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.5em;
`;

const Description = styled.p`
  color: #d7d7d7;
  margin: 0.75em 0 0;
  line-height: 1.4;
  font-size: 0.95em;
`;

const AssignedZone = styled.div`
  margin-top: 0.75em;
  padding: 0.75em;
  border-radius: 4px;
  background-color: #202731;
  color: #ffffff;
`;

const AssignedZoneMeta = styled.div`
  margin-top: 0.25em;
  color: #9cabbd;
  font-size: 0.9em;
`;

const ZoneCard = styled.div`
  margin-top: 1em;
  padding: 0.75em;
  border-radius: 4px;
  background-color: #181d25;
`;

const Label = styled.div`
  display: flex;
  margin-bottom: 0.35em;
  color: #d7d7d7;
`;

const Input = styled.input`
  box-sizing: border-box;
  background-color: #2a3342;
  border: 1px solid #3c4759;
  color: #ffffff;
  outline: none;
  font-size: 15px;
  margin: 0 0 0.5em;
  padding: 7px 8px;
  width: 100%;
  border-radius: 4px;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease;

  :hover {
    background-color: #313c4d;
    border-color: #55657d;
  }

  :focus {
    background-color: #313c4d;
    border-color: #0adb93;
  }
`;

const Select = styled.select`
  box-sizing: border-box;
  background-color: #181d25;
  color: #fff;
  padding: 6px 4px;
  border: none;
  width: 100%;
  margin-bottom: 0.5em;
  border-radius: 4px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5em;
  margin-top: 1em;
`;

const ActionButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${({ danger }) => (danger ? "#4d1f2a" : "#282f3c")};
  color: #fff;
  margin-top: 0.5em;

  :hover {
    background-color: ${({ danger }) => (danger ? "#db0a41" : "#0adb93")};
    color: ${({ danger }) => (danger ? "#fff" : "#000")};
  }
`;

const GraphicsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35em;
  margin: 0.35em 0 0.75em;
`;

const GraphicItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5em;
  padding: 0.45em 0.6em;
  border-radius: 4px;
  background-color: #202731;
  color: #d7d7d7;
  font-size: 0.9em;
`;

const GraphicInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15em;
`;

const GraphicMeta = styled.span`
  color: #9cabbd;
  font-size: 0.85em;
`;

const EmptyState = styled.div`
  color: #9cabbd;
  font-size: 0.9em;
  margin: 0.35em 0 0.75em;
`;

const PopupContent = styled.div`
  padding: 0 1em 1em;
`;

const Save = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5em;
  background-color: #282f3c;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 1em;

  :hover {
    color: #fff;
    background-color: #db0a41;
  }
`;

const TEMPLATE_SOURCES = [
  { key: "football", label: "Football", bucket: ["variables"] },
  { key: "rugby", label: "Rugby", bucket: ["rugby", "variables"] },
  {
    key: "basketball",
    label: "Basketball",
    bucket: ["basketball", "variables"],
  },
  { key: "gaa", label: "GAA", bucket: ["gaa", "variables"] },
  { key: "netball", label: "Netball", bucket: ["netball", "variables"] },
  { key: "climbing", label: "Climbing", bucket: ["climbing", "variables"] },
];

export default function Drive({ scene }) {
  const [isDriveMasterScene, setIsDriveMasterScene] = useState(false);
  const [zones, setZones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [templatePickerZoneId, setTemplatePickerZoneId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [showZoneAssignDialog, setShowZoneAssignDialog] = useState(false);
  const [selectedZoneName, setSelectedZoneName] = useState("");

  useEffect(() => {
    setIsDriveMasterScene(scene?.drive_master_scene === true);
    setZones(scene?.drive?.zones || []);
  }, [scene]);

  if (!scene) {
    return <div />;
  }

  async function save() {
    if (!scene?.project) {
      return;
    }

    setSaving(true);

    if (isDriveMasterScene) {
      scene.project.scenes.forEach((projectScene) => {
        projectScene.drive_master_scene = projectScene.name === scene.name;
      });
    } else {
      scene.drive_master_scene = false;
    }

    await scene.project.save();
    setSaving(false);
  }

  function updateZones(nextZones) {
    setZones(nextZones);
    scene.drive = {
      ...(scene.drive || {}),
      zones: nextZones,
    };
  }

  function addZone() {
    updateZones([
      ...zones,
      {
        id: `zone_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
        name: `Zone ${zones.length + 1}`,
        node_uuid: "",
      },
    ]);
  }

  function getGroupNodes(children, path = "", items = []) {
    children.forEach((child) => {
      const label = path ? `${path} / ${child.name}` : child.name;

      if (child.type === "GROUP") {
        items.push({
          label,
          value: child.uuid,
        });
      }

      if (child.children?.length > 0) {
        getGroupNodes(child.children, label, items);
      }
    });

    return items;
  }

  function getControlNodes(children, controlNodes = []) {
    children.forEach((child) => {
      if (child.control && child.control.name !== "") {
        controlNodes.push(child);
      }

      if (child.children?.length > 0) {
        getControlNodes(child.children, controlNodes);
      }
    });

    return controlNodes;
  }

  function getProjectScenes() {
    return scene?.project?.scenes || [scene];
  }

  function getDriveMasterScene() {
    return scene?.project?.scenes?.find((projectScene) => projectScene.drive_master_scene);
  }

  function getTemplateVariableEntries(node, bucketPath) {
    const variables = node?.control?.data;

    if (!variables) {
      return [];
    }

    if (bucketPath.length === 1) {
      return variables?.[bucketPath[0]] || [];
    }

    return variables?.[bucketPath[0]]?.[bucketPath[1]] || [];
  }

  function isZoneField(fieldName) {
    return (fieldName || "").toString().trim().toLowerCase() === "zone";
  }

  function getAllTemplates() {
    return getProjectScenes().flatMap((projectScene) =>
      TEMPLATE_SOURCES.flatMap((source) => {
        const templates = projectScene?.[source.key]?.templates || [];
        const controlNodes = getControlNodes(
          projectScene.scene?.children || [],
        );

        return templates.map((template) => {
          const hasZoneVariable = controlNodes.some((node) =>
            getTemplateVariableEntries(node, source.bucket).some(
              (entry) =>
                entry.template === template.name && isZoneField(entry.field),
            ),
          );

          return {
            appKey: source.key,
            appLabel: source.label,
            name: template.name,
            sceneName: projectScene.name,
            sceneRef: projectScene,
            key: `${projectScene.name}:${source.key}:${template.name}`,
            hasZoneVariable,
          };
        });
      }),
    );
  }

  function getZoneGraphics(zoneName) {
    return getAllTemplates().filter((template) =>
      getControlNodes(template.sceneRef.scene?.children || []).some((node) =>
        getTemplateVariableEntries(
          node,
          TEMPLATE_SOURCES.find((source) => source.key === template.appKey)
            ?.bucket || ["variables"],
        ).some(
          (entry) =>
            entry.template === template.name &&
            isZoneField(entry.field) &&
            entry.default === zoneName,
        ),
      ),
    );
  }

  function getAvailableTemplatesForZone(zoneName) {
    const assignedTemplateKeys = new Set(
      getZoneGraphics(zoneName).map((template) => template.key),
    );

    return getAllTemplates().filter(
      (template) => !assignedTemplateKeys.has(template.key),
    );
  }

  const selectedZone = zones.find((zone) => zone.id === templatePickerZoneId);
  const availableTemplates = selectedZone
    ? getAvailableTemplatesForZone(selectedZone.name)
    : [];
  const masterScene = getDriveMasterScene();
  const masterZones = masterScene?.drive?.zones || [];

  const groupNodeOptions = getGroupNodes(scene.scene?.children || []);

  function assignTemplateToZone({ zone, scene }) {
    scene.zone = zone?.name;

    setTemplatePickerZoneId(null);
  }

  function removeSceneFromZone(graphicScene) {
    graphicScene.zone = null;
    setZones([...zones]);
  }

  return (
    <Main>
      <Group>
        <Title>Drive</Title>
        <Row>
          <label className={textStyles.CheckboxContainer}>
            Drive master scene
            <input
              type="checkbox"
              checked={isDriveMasterScene}
              onChange={(e) => {
                const checked = e.currentTarget.checked;
                setIsDriveMasterScene(checked);
                scene.drive_master_scene = checked;
              }}
            />
            <span className={textStyles.Checkmark} />
          </label>
        </Row>
        <Description>
          Mark this scene as the Drive master scene. Saving will clear this flag
          from any other scene in the project.
        </Description>
        {!isDriveMasterScene && (
          <AssignedZone>
            <div>Assigned Zone: {scene.zone || "None"}</div>
            <AssignedZoneMeta>
              This scene will appear in the selected zone on the Drive master
              scene.
            </AssignedZoneMeta>
          </AssignedZone>
        )}
        <ButtonRow>
          {isDriveMasterScene && (
            <ActionButton onClick={() => addZone()}>+ Add Zone</ActionButton>
          )}
          {!isDriveMasterScene && (
            <ActionButton
              onClick={() => {
                setSelectedZoneName(scene.zone || masterZones[0]?.name || "");
                setShowZoneAssignDialog(true);
              }}
            >
              Add To Zone
            </ActionButton>
          )}
        </ButtonRow>
        {isDriveMasterScene &&
          zones.map((zone, index) => {
            const zoneGraphics = scene.project.scenes?.filter(
              (s) => s?.zone === zone.name,
            );

            return (
              <ZoneCard key={zone.id || index}>
                <Label>Zone Name</Label>
                <Input
                  value={zone.name || ""}
                  onChange={(e) => {
                    updateZones(
                      zones.map((currentZone, zoneIndex) =>
                        zoneIndex === index
                          ? { ...currentZone, name: e.currentTarget.value }
                          : currentZone,
                      ),
                    );
                  }}
                />
                <Label>Associated Group Node</Label>
                <Select
                  value={zone.node_uuid || ""}
                  onChange={(e) => {
                    updateZones(
                      zones.map((currentZone, zoneIndex) =>
                        zoneIndex === index
                          ? { ...currentZone, node_uuid: e.currentTarget.value }
                          : currentZone,
                      ),
                    );
                  }}
                >
                  <option value="">No group selected</option>
                  {groupNodeOptions.map((option) => {
                    return (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    );
                  })}
                </Select>
                <Label>Graphics</Label>
                {zoneGraphics.length === 0 && (
                  <EmptyState>No graphics assigned to this zone.</EmptyState>
                )}
                {zoneGraphics.length > 0 && (
                  <GraphicsList>
                    {zoneGraphics.map((graphic) => {
                      return (
                        <GraphicItem key={graphic._id || graphic.name}>
                          <GraphicInfo>
                            <span>{graphic.name}</span>
                          </GraphicInfo>
                          <ActionButton
                            danger={true}
                            onClick={() => {
                              removeSceneFromZone(graphic);
                            }}
                          >
                            Remove
                          </ActionButton>
                        </GraphicItem>
                      );
                    })}
                  </GraphicsList>
                )}
                <ActionButton
                  onClick={() => {
                    setTemplatePickerZoneId(zone.id);
                  }}
                >
                  + Add Existing Template
                </ActionButton>
                <ActionButton
                  danger={true}
                  onClick={() => {
                    updateZones(
                      zones.filter((_, zoneIndex) => zoneIndex !== index),
                    );
                  }}
                >
                  Remove Zone
                </ActionButton>
              </ZoneCard>
            );
          })}
        <Save onClick={() => save()}>
          {saving === true ? "Saving.." : "Save Drive Settings"}
          <ClipLoader color={"#ffffff"} loading={saving === true} size={15} />
        </Save>
      </Group>
      {templatePickerZoneId && (
        <Popup
          title="Add Template To Zone"
          message="Select an existing template. Confirming will set that template's Zone variable to this zone name."
          onCancel={() => {
            setTemplatePickerZoneId(null);
            setSelectedTemplate(null);
          }}
          onSave={() => {
            const zone = zones.find((item) => item.id === templatePickerZoneId);
            if (zone) {
              assignTemplateToZone({ zone, scene: selectedTemplate });
            }
          }}
          confirmButton="Assign"
          showConfirmButton={availableTemplates.length > 0}
        >
          <PopupContent>
            {availableTemplates.length === 0 && (
              <EmptyState>No templates are available for this zone.</EmptyState>
            )}
            {availableTemplates.length > 0 && (
              <Select
                value={selectedTemplate?._id}
                onChange={(event) => {
                  setSelectedTemplate(
                    scene.project.scenes?.find(
                      (s) => s?._id === event.currentTarget.value,
                    ),
                  );
                }}
              >
                {scene.project.scenes
                  ?.filter(
                    (s) =>
                      s?.zone !==
                        zones.find((item) => item.id === templatePickerZoneId)
                          ?.name && !s?.drive_master_scene,
                  )
                  .map((template) => {
                    return (
                      <option key={template._id} value={template._id}>
                        {template.name}
                      </option>
                    );
                  })}
              </Select>
            )}
          </PopupContent>
        </Popup>
      )}
      {showZoneAssignDialog && (
        <Popup
          title="Add To Zone"
          message="Select a zone from the Drive master scene."
          onCancel={() => {
            setShowZoneAssignDialog(false);
          }}
          onSave={() => {
            scene.zone = selectedZoneName || null;
            setShowZoneAssignDialog(false);
          }}
          confirmButton="Assign"
          showConfirmButton={masterZones.length > 0}
        >
          <PopupContent>
            {masterZones.length === 0 && (
              <EmptyState>No zones are available on the Drive master scene.</EmptyState>
            )}
            {masterZones.length > 0 && (
              <Select
                value={selectedZoneName}
                onChange={(event) => {
                  setSelectedZoneName(event.currentTarget.value);
                }}
              >
                {masterZones.map((zone) => {
                  return (
                    <option key={zone.id || zone.name} value={zone.name}>
                      {zone.name}
                    </option>
                  );
                })}
              </Select>
            )}
          </PopupContent>
        </Popup>
      )}
    </Main>
  );
}
